import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Handshake, Plus, Clock, ShoppingCart, Eye, Search, Download, Printer } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatCard } from '@/components/ui/StatCard';
import { Tabs } from '@/components/ui/Tabs';
import { Input } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { NewOrderModal } from '@/components/orders/NewOrderModal';
import { supabase } from '@/lib/supabaseClient';
import { exportToCsv } from '@/lib/exportCsv';
import { useTabState } from '@/hooks/useTabState';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface Reseller {
  id: string;
  serial_no: number;
  type: string;
  name: string;
  district: string | null;
  contact: string | null;
  status: string;
}

interface Application {
  id: string;
  business_name: string;
  business_type: string | null;
  status: string;
  rejection_reason: string | null;
  created_at: string;
}

interface ArLedgerRow {
  status: string;
  amount_bdt: number;
  amount_paid_bdt: number;
}

interface ResellerOrder {
  id: string;
  order_no: string;
  status: string;
  total_bdt: number;
  placed_at: string;
  partner_id: string | null;
  is_gift: boolean;
  core_ar_ledger: ArLedgerRow | ArLedgerRow[] | null;
}

type PaymentStatus = 'gift' | 'paid' | 'unpaid';

// Gift orders never get an AR ledger row at all (nothing owed). A real sale
// does — 'paid' covers ar status='paid' and 'returned' alike, since a return
// zeroes out the remaining balance the same way a cash payment would.
function paymentStatusOf(o: ResellerOrder): PaymentStatus {
  if (o.is_gift) return 'gift';
  const ar = Array.isArray(o.core_ar_ledger) ? o.core_ar_ledger[0] : o.core_ar_ledger;
  if (!ar) return 'paid';
  return ar.amount_paid_bdt >= ar.amount_bdt ? 'paid' : 'unpaid';
}

const STATUS_BADGE: Record<string, { tone: 'warning' | 'info' | 'error'; label: string }> = {
  pending: { tone: 'warning', label: 'Pending review' },
  forwarded: { tone: 'info', label: 'Forwarded to ERP' },
  rejected: { tone: 'error', label: 'Cancelled' },
};

const ORDER_STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  placed: 'primary',
  confirmed: 'primary',
  shipped: 'warning',
  dispatched: 'warning',
  delivered: 'success',
  cod_collected: 'success',
  remitted: 'success',
  settled: 'success',
  cancelled: 'error',
  returned: 'error',
};

type TabKey = 'applications' | 'directory' | 'orders';

export default function AdminResellers() {
  const navigate = useNavigate();
  const [tab, setTab] = useTabState<TabKey>('applications', ['applications', 'directory', 'orders']);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [orders, setOrders] = useState<ResellerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [directorySearch, setDirectorySearch] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | 'sale' | 'gift'>('all');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [monthFilter, setMonthFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [partnerRes, applicationRes, orderRes] = await Promise.all([
      supabase.from('core_partners').select('id, serial_no, type, name, district, contact, status').order('serial_no'),
      supabase
        .from('core_partner_applications')
        .select('id, business_name, business_type, status, rejection_reason, created_at')
        .neq('status', 'approved')
        .order('created_at', { ascending: false }),
      supabase
        .from('core_orders')
        .select('id, order_no, status, total_bdt, placed_at, partner_id, is_gift, core_ar_ledger(status, amount_bdt, amount_paid_bdt)')
        .eq('channel', 'pharmacy_b2b')
        .order('placed_at', { ascending: false })
        .limit(100),
    ]);
    setResellers((partnerRes.data as Reseller[]) ?? []);
    setApplications((applicationRes.data as Application[]) ?? []);
    setOrders((orderRes.data as ResellerOrder[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredResellers = useMemo(() => {
    const term = directorySearch.trim().toLowerCase();
    if (!term) return resellers;
    return resellers.filter((r) => [r.name, r.type, r.contact, r.district].filter(Boolean).some((field) => field!.toLowerCase().includes(term)));
  }, [resellers, directorySearch]);

  const downloadResellersCsv = () => {
    exportToCsv(
      `resellers-directory_${new Date().toISOString().slice(0, 10)}.csv`,
      ['#', 'Name', 'Type', 'Contact', 'District', 'Status'],
      filteredResellers.map((r) => [String(r.serial_no), r.name ?? '', r.type ?? '', r.contact ?? '', r.district ?? '', r.status ?? '']),
    );
  };

  const resellerName = (partnerId: string | null) => resellers.find((r) => r.id === partnerId)?.name ?? '—';
  const orderCounts = {
    total: orders.length,
    inProgress: orders.filter((o) => ['placed', 'confirmed'].includes(o.status)).length,
    shipped: orders.filter((o) => ['shipped', 'dispatched'].includes(o.status)).length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (orderTypeFilter === 'sale' && o.is_gift) return false;
      if (orderTypeFilter === 'gift' && !o.is_gift) return false;
      if (paymentFilter !== 'all' && paymentStatusOf(o) !== paymentFilter) return false;
      if (monthFilter && !o.placed_at.startsWith(monthFilter)) return false;
      return true;
    });
  }, [orders, orderTypeFilter, paymentFilter, monthFilter]);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Reseller</h1>
          <Button size="sm" onClick={() => navigate('/admin/resellers/new')}>
            <Plus className="w-3.5 h-3.5" /> Add Reseller
          </Button>
        </div>

        <Tabs
          active={tab}
          onChange={(k) => setTab(k as TabKey)}
          tabs={[
            { key: 'applications', label: 'Applications', badge: applications.filter((a) => a.status !== 'rejected').length },
            { key: 'directory', label: 'Directory' },
            { key: 'orders', label: 'Orders' },
          ]}
        />

        {loading ? (
          <Spinner />
        ) : (
          <>
            {tab === 'applications' && (
              <Card>
                <CardHeader title="Pending / Recent Applications" subtitle={`${applications.length}`} icon={<Clock className="w-4 h-4" />} />
                <CardBody className="space-y-1">
                  {applications.length === 0 ? (
                    <EmptyState title="No applications yet" />
                  ) : (
                    applications.map((a) => {
                      const badge = STATUS_BADGE[a.status] ?? { tone: 'warning' as const, label: a.status };
                      return (
                        <button
                          key={a.id}
                          onClick={() => navigate(`/admin/resellers/applications/${a.id}`)}
                          className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted text-left cursor-pointer"
                        >
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold text-ink truncate">{a.business_name || 'Untitled application'}</p>
                            <p className="text-[11.5px] text-ink-faint truncate">
                              {a.business_type ?? '—'}
                              {a.status === 'rejected' && a.rejection_reason && ` · ${a.rejection_reason}`}
                            </p>
                          </div>
                          <Badge tone={badge.tone}>{badge.label}</Badge>
                        </button>
                      );
                    })
                  )}
                </CardBody>
              </Card>
            )}

            {tab === 'directory' && (
              <Card>
                <CardHeader
                  title="Directory"
                  subtitle={directorySearch.trim() ? `${filteredResellers.length} of ${resellers.length} partners` : `${resellers.length} partners`}
                  icon={<Handshake className="w-4 h-4" />}
                  action={
                    <Button variant="secondary" size="sm" onClick={downloadResellersCsv} disabled={filteredResellers.length === 0}>
                      <Download className="w-3.5 h-3.5" /> Download CSV
                    </Button>
                  }
                />
                <CardBody className="space-y-3">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                    <Input placeholder="Search by name, type, contact, district..." value={directorySearch} onChange={(e) => setDirectorySearch(e.target.value)} className="pl-8" />
                  </div>
                  {filteredResellers.length === 0 ? (
                    <EmptyState title={resellers.length === 0 ? 'No resellers yet' : 'No partners match your search'} hint={resellers.length === 0 ? 'Add one above — it goes live here once approved in ERP.' : undefined} />
                  ) : (
                    <div className="space-y-1">
                      {filteredResellers.map((r) => (
                        <div key={r.id} className="flex items-center gap-3 justify-between px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150">
                          <span className="text-[11px] font-semibold text-ink-faint flex-shrink-0 w-6 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {r.serial_no}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-semibold text-ink truncate">{r.name || 'Untitled partner'}</p>
                            <p className="text-[11.5px] text-ink-faint truncate">
                              {r.type}
                              {r.district && ` · ${r.district}`}
                              {r.contact && ` · ${r.contact}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge tone={r.status === 'active' ? 'success' : 'neutral'}>{r.status}</Badge>
                            <button
                              onClick={() => navigate(`/admin/resellers/${r.id}/view`)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer"
                              title="View profile"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {tab === 'orders' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="Total" value={orderCounts.total} accent="primary" />
                  <StatCard label="In Progress" value={orderCounts.inProgress} accent="warning" />
                  <StatCard label="Shipped" value={orderCounts.shipped} accent="info" />
                  <StatCard label="Delivered" value={orderCounts.delivered} accent="success" />
                </div>
                <Card>
                  <CardHeader
                    title="Reseller Orders"
                    subtitle={filteredOrders.length === orders.length ? `${orders.length} shown` : `${filteredOrders.length} of ${orders.length} shown`}
                    icon={<ShoppingCart className="w-4 h-4" />}
                    action={
                      <div className="flex items-center flex-wrap justify-end gap-2">
                        <div className="inline-flex items-center gap-1 bg-surface-muted rounded-xl p-1">
                          {(
                            [
                              ['all', 'All'],
                              ['sale', 'Sale'],
                              ['gift', '🎁 Gift'],
                            ] as const
                          ).map(([key, label]) => (
                            <button
                              key={key}
                              onClick={() => setOrderTypeFilter(key)}
                              className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold cursor-pointer transition-colors ${
                                orderTypeFilter === key ? 'bg-white text-primary-800 shadow-sm' : 'text-ink-faint hover:text-ink'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="inline-flex items-center gap-1 bg-surface-muted rounded-xl p-1">
                          {(
                            [
                              ['all', 'All'],
                              ['paid', 'Paid'],
                              ['unpaid', 'Unpaid'],
                            ] as const
                          ).map(([key, label]) => (
                            <button
                              key={key}
                              onClick={() => setPaymentFilter(key)}
                              className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold cursor-pointer transition-colors ${
                                paymentFilter === key ? 'bg-white text-primary-800 shadow-sm' : 'text-ink-faint hover:text-ink'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="!w-36" />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            const params = new URLSearchParams({ type: orderTypeFilter, payment: paymentFilter });
                            if (monthFilter) params.set('month', monthFilter);
                            navigate(`/admin/resellers/orders-print?${params.toString()}`);
                          }}
                        >
                          <Printer className="w-3.5 h-3.5" /> Print
                        </Button>
                        <Button size="sm" onClick={() => setNewOrderOpen(true)}>
                          <Plus className="w-3.5 h-3.5" /> New Order
                        </Button>
                      </div>
                    }
                  />
                  <CardBody className="space-y-1">
                    {filteredOrders.length === 0 ? (
                      <EmptyState title={orders.length === 0 ? 'No reseller orders yet' : 'No orders match this filter'} />
                    ) : (
                      filteredOrders.map((o) => {
                        const payment = paymentStatusOf(o);
                        return (
                          <button
                            key={o.id}
                            onClick={() => navigate(`/admin/orders/${o.id}`)}
                            className="w-full flex items-center justify-between gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted text-left cursor-pointer"
                          >
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-ink truncate">{o.order_no}</p>
                              <p className="text-[11.5px] text-ink-faint truncate">
                                {resellerName(o.partner_id)} · {new Date(o.placed_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge tone={ORDER_STATUS_TONE[o.status] ?? 'neutral'}>{o.status}</Badge>
                              {payment === 'gift' ? (
                                <Badge tone="neutral">🎁 gift</Badge>
                              ) : (
                                <Badge tone={payment === 'paid' ? 'success' : 'warning'}>{payment}</Badge>
                              )}
                              <span className="text-[12.5px] font-semibold text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                ৳{o.total_bdt.toLocaleString()}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </CardBody>
                </Card>
              </div>
            )}
          </>
        )}
      </div>

      <NewOrderModal open={newOrderOpen} onClose={() => setNewOrderOpen(false)} onCreated={load} defaultReseller />
    </AppShell>
  );
}
